import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileContract,
  faEdit,
  faSearch,
  faSync,
  faCheck,
  faExclamationTriangle,
  faSave,
  faBuilding,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faChevronLeft,
  faChevronRight,
  faFilter,
  faChartLine,
  faUpload,
  faDownload,
  faCalendarAlt,
  faMoneyBillWave,
  faTrash,
  faBell
} from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import { useDashboardAuth } from '../../context/DashboardAuthContext';
import './Subscriptions.css';

// Format date as YYYY-MM-DD using local timezone (avoids UTC shift from toISOString)
const formatLocalDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Parse date string as local date (avoids UTC midnight shift from new Date(string))
const parseLocalDate = (s) => {
  if (!s) return new Date();
  const d = s.split('T')[0].split('-');
  return new Date(parseInt(d[0]), parseInt(d[1]) - 1, parseInt(d[2]));
};

const Subscriptions = () => {
  const { admin } = useDashboardAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [cancellationData, setCancellationData] = useState({
    subscription_id: null,
    cancellation_reason: '',
    cancellation_date: formatLocalDate(new Date())
  });
  const [editingGroup, setEditingGroup] = useState(null);
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [uploadingContract, setUploadingContract] = useState(null);
  const [deletingContract, setDeletingContract] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const fileInputRef = useRef(null);
  const itemsPerPage = 6;

  // Expanded subscription for mobile
  const [expandedSubId, setExpandedSubId] = useState(null);

  const toggleSubExpand = (groupId) => {
    setExpandedSubId(prev => prev === groupId ? null : groupId);
  };

  const [formData, setFormData] = useState({
    group_id: '',
    group_name: '',
    admin_id: '',
    admin_name: '',
    montant: '',
    montant_preabonnement: '',
    statut: 'Active',
    contract_start_date: '',
    contract_end_date: '',
    notes: '',
    cancellation_reason: '',
    cancellation_date: ''
  });

  const loadSubscriptions = useCallback(async () => {
    try {
      const response = await api.get('/dashboard-admin/subscriptions');
      // Subscriptions loaded
      setSubscriptions(response.data || []);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    }
  }, []);

  const loadAdmins = useCallback(async () => {
    try {
      const response = await api.get('/dashboard-admin/admins');
      // Admins loaded
      setAdmins(response.data || []);
    } catch (error) {
      console.error('Error loading admins:', error);
    }
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      const response = await api.get('/dashboard-admin/groups');
      // Groups loaded
      setGroups(response.data || []);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadGroups(), loadSubscriptions(), loadAdmins()]);
    setLoading(false);
  }, [loadGroups, loadSubscriptions, loadAdmins]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  // Merge groups with their subscriptions
  const getGroupsWithSubscriptions = () => {
    return groups.map(group => {
      // group.id is a UUID string, subscription.group_id should also be a string
      const subscription = subscriptions.find(s => String(s.group_id) === String(group.id));
      return {
        ...group,
        subscription: subscription || null,
        hasSubscription: !!subscription
      };
    });
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    if (group.subscription) {
      setFormData({
        group_id: group.id,
        group_name: group.nom,
        admin_id: group.subscription.admin_id || '',
        admin_name: group.subscription.admin_name || '',
        montant: group.subscription.montant || '',
        montant_preabonnement: group.subscription.montant_preabonnement || '',
        statut: group.subscription.statut || 'Active',
        contract_start_date: group.subscription.contract_start_date 
          ? group.subscription.contract_start_date.split('T')[0] 
          : '',
        contract_end_date: group.subscription.contract_end_date 
          ? group.subscription.contract_end_date.split('T')[0] 
          : '',
        notes: group.subscription.notes || '',
        cancellation_reason: group.subscription.cancellation_reason || '',
        cancellation_date: group.subscription.cancellation_date 
          ? group.subscription.cancellation_date.split('T')[0] 
          : ''
      });
    } else {
      // Auto-fill with current admin for new subscriptions
      setFormData({
        group_id: group.id,
        group_name: group.nom,
        admin_id: admin?.id || '',
        admin_name: admin?.name || '',
        montant: '',
        montant_preabonnement: '',
        statut: 'Active',
        contract_start_date: '',
        contract_end_date: '',
        notes: '',
        cancellation_reason: '',
        cancellation_date: ''
      });
    }
    setShowModal(true);
  };

  // Handle status change - show cancellation modal if changing to "Annulé"
  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    if (newStatus === 'Annulé' && formData.statut !== 'Annulé') {
      // Show cancellation modal
      setCancellationData({
        subscription_id: editingGroup?.subscription?.id,
        cancellation_reason: '',
        cancellation_date: formatLocalDate(new Date())
      });
      setShowCancellationModal(true);
    } else {
      setFormData({ ...formData, statut: newStatus });
    }
  };

  // Confirm cancellation
  const handleConfirmCancellation = () => {
    setFormData({
      ...formData,
      statut: 'Annulé',
      cancellation_reason: cancellationData.cancellation_reason,
      cancellation_date: cancellationData.cancellation_date
    });
    setShowCancellationModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGroup.subscription) {
        // Update existing subscription
        await api.put(`/dashboard-admin/subscriptions/${editingGroup.subscription.id}`, formData);
        showNotification('success', 'Abonnement mis à jour');
      } else {
        // Create new subscription
        await api.post('/dashboard-admin/subscriptions', formData);
        showNotification('success', 'Abonnement créé');
      }
      setShowModal(false);
      resetForm();
      loadSubscriptions();
    } catch (error) {
      console.error('Error saving subscription:', error);
      showNotification('error', 'Erreur lors de l\'enregistrement');
    }
  };

  const handleContractUpload = async (subscriptionId, file) => {
    if (!file) return;
    
    setUploadingContract(subscriptionId);
    const uploadData = new FormData();
    uploadData.append('contract', file);
    
    try {
      await api.post(`/dashboard-admin/subscriptions/${subscriptionId}/contract`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showNotification('success', 'Contrat téléversé avec succès');
      loadSubscriptions();
    } catch (error) {
      console.error('Error uploading contract:', error);
      showNotification('error', 'Erreur lors du téléversement du contrat');
    } finally {
      setUploadingContract(null);
    }
  };

  const handleContractDelete = async (subscriptionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contrat PDF ?')) return;
    
    setDeletingContract(subscriptionId);
    try {
      await api.delete(`/dashboard-admin/subscriptions/${subscriptionId}/contract`);
      showNotification('success', 'Contrat supprimé avec succès');
      loadSubscriptions();
    } catch (error) {
      console.error('Error deleting contract:', error);
      showNotification('error', 'Erreur lors de la suppression du contrat');
    } finally {
      setDeletingContract(null);
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return '-';
    const formatted = parseFloat(amount).toFixed(0);
    return formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' FCFA';
  };

  const resetForm = () => {
    setEditingGroup(null);
    setFormData({
      group_id: '',
      group_name: '',
      admin_id: '',
      admin_name: '',
      montant: '',
      montant_preabonnement: '',
      statut: 'Active',
      contract_start_date: '',
      contract_end_date: '',
      notes: '',
      cancellation_reason: '',
      cancellation_date: ''
    });
    setCancellationData({
      cancellation_reason: '',
      cancellation_date: ''
    });
  };

  const groupsWithSubs = getGroupsWithSubscriptions();

  // Check if subscription expires within 3 months
  const isExpiringSoon = (subscription) => {
    if (!subscription || !subscription.contract_end_date) return false;
    if (subscription.statut !== 'Active') return false;
    
    const endDate = parseLocalDate(subscription.contract_end_date);
    const today = new Date();
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
    
    return endDate > today && endDate <= threeMonthsFromNow;
  };

  // Count expiring soon
  const expiringSoonCount = groupsWithSubs.filter(g => isExpiringSoon(g.subscription)).length;
  
  // Filter by search and status
  const filteredGroups = groupsWithSubs.filter(group => {
    const matchesSearch = group.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.subscription?.admin_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (statusFilter === 'all') return matchesSearch;
    if (statusFilter === 'non-abonne') return matchesSearch && !group.hasSubscription;
    if (statusFilter === 'Active') return matchesSearch && group.subscription?.statut === 'Active';
    if (statusFilter === 'En attente') return matchesSearch && group.subscription?.statut === 'En attente';
    if (statusFilter === 'Expiré') return matchesSearch && group.subscription?.statut === 'Expiré';
    if (statusFilter === 'Annulé') return matchesSearch && group.subscription?.statut === 'Annulé';
    if (statusFilter === 'expiring-soon') return matchesSearch && isExpiringSoon(group.subscription);
    
    return matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredGroups.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedGroups = filteredGroups.slice(startIndex, startIndex + itemsPerPage);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const getStatusInfo = (subscription) => {
    if (!subscription) {
      return { icon: faTimesCircle, class: 'status-none', text: 'Non abonné' };
    }
    
    const statusMap = {
      'Active': { icon: faCheckCircle, class: 'status-success', text: 'Actif' },
      'En attente': { icon: faClock, class: 'status-warning', text: 'En attente' },
      'Expiré': { icon: faExclamationTriangle, class: 'status-expired', text: 'Expiré' },
      'Annulé': { icon: faTimesCircle, class: 'status-danger', text: 'Annulé' }
    };
    
    return statusMap[subscription.statut] || { icon: faClock, class: 'status-default', text: subscription.statut };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return parseLocalDate(dateStr).toLocaleDateString('fr-FR');
  };

  // Count stats
  const subscribedCount = groupsWithSubs.filter(g => g.hasSubscription).length;
  const activeCount = groupsWithSubs.filter(g => g.subscription?.statut === 'Active').length;
  const enAttenteCount = groupsWithSubs.filter(g => g.subscription?.statut === 'En attente').length;
  const expireCount = groupsWithSubs.filter(g => g.subscription?.statut === 'Expiré').length;
  const annuleCount = groupsWithSubs.filter(g => g.subscription?.statut === 'Annulé').length;

  // Calculate yearly revenue analytics
  const calculateYearlyRevenue = (year) => {
    const yearSubscriptions = subscriptions.filter(s => {
      if (!s.contract_start_date) return false;
      const startYear = parseLocalDate(s.contract_start_date).getFullYear();
      const endYear = s.contract_end_date ? parseLocalDate(s.contract_end_date).getFullYear() : new Date().getFullYear();
      return startYear <= year && endYear >= year && s.statut === 'Active';
    });

    const totalAnnuel = yearSubscriptions.reduce((sum, s) => sum + (parseFloat(s.montant) || 0), 0);
    const totalPreabonnement = yearSubscriptions.reduce((sum, s) => sum + (parseFloat(s.montant_preabonnement) || 0), 0);
    
    return {
      abonnements: totalAnnuel,
      preabonnements: totalPreabonnement,
      total: totalAnnuel + totalPreabonnement,
      count: yearSubscriptions.length
    };
  };

  const yearlyRevenue = calculateYearlyRevenue(selectedYear);
  
  // Get available years from subscriptions
  const getAvailableYears = () => {
    const years = new Set();
    const currentYear = new Date().getFullYear();
    years.add(currentYear);
    
    subscriptions.forEach(s => {
      if (s.contract_start_date) {
        years.add(parseLocalDate(s.contract_start_date).getFullYear());
      }
      if (s.contract_end_date) {
        years.add(parseLocalDate(s.contract_end_date).getFullYear());
      }
    });
    
    return Array.from(years).sort((a, b) => b - a);
  };

  return (
    <div className="subscriptions-page">
      {notification && (
        <div className={`notification ${notification.type}`}>
          <FontAwesomeIcon icon={notification.type === 'success' ? faCheck : faExclamationTriangle} />
          {notification.message}
        </div>
      )}

      <div className="page-header">
        <div>
          <h1><FontAwesomeIcon icon={faFileContract} /> Abonnements Clients</h1>
          <p>Gestion des abonnements au logiciel de gestion</p>
        </div>
        <div className="stats-badges">
          <span className="stat-badge" onClick={() => setStatusFilter('all')} style={{ cursor: 'pointer' }}>
            <FontAwesomeIcon icon={faBuilding} /> {groups.length} Groupes
          </span>
          <span className="stat-badge success" onClick={() => setStatusFilter('Active')} style={{ cursor: 'pointer' }}>
            <FontAwesomeIcon icon={faCheckCircle} /> {activeCount} Actifs
          </span>
          <span className="stat-badge alert" onClick={() => setStatusFilter('expiring-soon')} style={{ cursor: 'pointer' }}>
            <FontAwesomeIcon icon={faBell} /> {expiringSoonCount} Expire bientôt
          </span>
          <span className="stat-badge pending" onClick={() => setStatusFilter('En attente')} style={{ cursor: 'pointer' }}>
            <FontAwesomeIcon icon={faClock} /> {enAttenteCount} En attente
          </span>
          <span className="stat-badge expired" onClick={() => setStatusFilter('Expiré')} style={{ cursor: 'pointer' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} /> {expireCount} Expirés
          </span>
          <span className="stat-badge cancelled" onClick={() => setStatusFilter('Annulé')} style={{ cursor: 'pointer' }}>
            <FontAwesomeIcon icon={faTimesCircle} /> {annuleCount} Annulés
          </span>
          <span className="stat-badge warning" onClick={() => setStatusFilter('non-abonne')} style={{ cursor: 'pointer' }}>
            <FontAwesomeIcon icon={faClock} /> {groups.length - subscribedCount} Non abonnés
          </span>
        </div>
      </div>

      {/* Revenue Analytics Section */}
      <div className="revenue-analytics">
        <div className="analytics-header">
          <h3><FontAwesomeIcon icon={faChartLine} /> Analyse des Revenus</h3>
          <div className="year-selector">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <select 
              value={selectedYear} 
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {getAvailableYears().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="analytics-cards">
          <div className="analytics-card">
            <div className="analytics-icon abonnement">
              <FontAwesomeIcon icon={faMoneyBillWave} />
            </div>
            <div className="analytics-content">
              <span className="analytics-label">Abonnements Annuels</span>
              <span className="analytics-value">{formatAmount(yearlyRevenue.abonnements)}</span>
            </div>
          </div>
          <div className="analytics-card">
            <div className="analytics-icon preabonnement">
              <FontAwesomeIcon icon={faFileContract} />
            </div>
            <div className="analytics-content">
              <span className="analytics-label">Pré-Abonnements</span>
              <span className="analytics-value">{formatAmount(yearlyRevenue.preabonnements)}</span>
            </div>
          </div>
          <div className="analytics-card total">
            <div className="analytics-icon total">
              <FontAwesomeIcon icon={faChartLine} />
            </div>
            <div className="analytics-content">
              <span className="analytics-label">Total {selectedYear}</span>
              <span className="analytics-value">{formatAmount(yearlyRevenue.total)}</span>
              <span className="analytics-subtitle">{yearlyRevenue.count} abonnements actifs</span>
            </div>
          </div>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            placeholder="Rechercher un groupe..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-select">
          <FontAwesomeIcon icon={faFilter} />
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tous les statuts</option>
            <option value="Active">Actifs</option>
            <option value="expiring-soon">⚠️ Expire bientôt ({expiringSoonCount})</option>
            <option value="En attente">En attente</option>
            <option value="Expiré">Expirés</option>
            <option value="Annulé">Annulés</option>
            <option value="non-abonne">Non abonnés</option>
          </select>
        </div>
        <button className="btn-refresh" onClick={loadAll} disabled={loading}>
          <FontAwesomeIcon icon={faSync} spin={loading} />
        </button>
      </div>

      <div className="subscriptions-table-container">
        <table className="subscriptions-table">
          <thead>
            <tr>
              <th>Groupe Client</th>
              <th>Statut</th>
              <th>Admin Responsable</th>
              <th>Montant Annuel</th>
              <th>Pré-Abonnement</th>
              <th>Période</th>
              <th>Contrat PDF</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="loading-cell">Chargement...</td>
              </tr>
            ) : paginatedGroups.length === 0 ? (
              <tr>
                <td colSpan="8" className="empty-cell">
                  <FontAwesomeIcon icon={faBuilding} size="2x" />
                  <p>Aucun groupe trouvé</p>
                </td>
              </tr>
            ) : (
              paginatedGroups.map(group => {
                const statusInfo = getStatusInfo(group.subscription);
                const isCancelled = group.subscription?.statut === 'Annulé';
                const expiringSoon = isExpiringSoon(group.subscription);
                return (
                  <React.Fragment key={group.id}>
                  <tr className={`sub-row ${expandedSubId === group.id ? 'expanded-row' : ''}`} onClick={() => toggleSubExpand(group.id)}>
                    <td>
                      <FontAwesomeIcon icon={faBuilding} className="group-icon" />
                      <strong>{group.nom}</strong>
                    </td>
                    <td>
                      <span 
                        className={`status-badge ${statusInfo.class}`}
                        title={group.subscription?.statut === 'Annulé' && (group.subscription?.cancellation_reason || group.subscription?.cancellation_date)
                          ? `Annulé le ${formatDate(group.subscription.cancellation_date) || '-'} - Raison: ${group.subscription.cancellation_reason || '-'}`
                          : undefined}
                      >
                        {statusInfo.text}
                      </span>
                    </td>
                    <td className="sub-admin-cell">
                      {group.subscription?.admin_name || <span className="text-muted">-</span>}
                    </td>
                    <td className="montant-cell sub-hide-mobile">
                      {formatAmount(group.subscription?.montant)}
                    </td>
                    <td className="preabonnement-cell sub-hide-mobile">
                      {formatAmount(group.subscription?.montant_preabonnement)}
                    </td>
                    <td className="periode-cell sub-hide-mobile">
                      {group.subscription?.contract_start_date ? (
                        <span className={isExpiringSoon(group.subscription) ? 'expiring-soon' : ''}>
                          {formatDate(group.subscription.contract_start_date)} - {formatDate(group.subscription.contract_end_date)}
                          {isExpiringSoon(group.subscription) && (
                            <span className="expiring-alert" title="Expire dans moins de 3 mois">
                              <FontAwesomeIcon icon={faBell} />
                            </span>
                          )}
                        </span>
                      ) : <span className="text-muted">-</span>}
                    </td>
                    <td className="contract-cell sub-hide-mobile">
                      {group.subscription ? (
                        group.subscription.contract_pdf_url ? (
                          <div className="contract-actions">
                            <a 
                              href={`${api.defaults.baseURL?.replace('/api', '')}${group.subscription.contract_pdf_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-download"
                              title="Télécharger le contrat"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FontAwesomeIcon icon={faDownload} />
                            </a>
                            <button
                              className="btn-delete-contract"
                              onClick={(e) => { e.stopPropagation(); handleContractDelete(group.subscription.id); }}
                              disabled={deletingContract === group.subscription.id}
                              title="Supprimer le contrat"
                            >
                              <FontAwesomeIcon icon={faTrash} spin={deletingContract === group.subscription.id} />
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn-upload"
                            onClick={(e) => {
                              e.stopPropagation();
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = '.pdf';
                              input.onchange = (ev) => handleContractUpload(group.subscription.id, ev.target.files[0]);
                              input.click();
                            }}
                            disabled={uploadingContract === group.subscription.id}
                          >
                            <FontAwesomeIcon icon={faUpload} spin={uploadingContract === group.subscription.id} />
                            {uploadingContract === group.subscription.id ? 'Envoi...' : 'Ajouter'}
                          </button>
                        )
                      ) : <span className="text-muted">-</span>}
                    </td>
                    <td className="sub-hide-mobile">
                      <button 
                        className={`btn-action ${group.hasSubscription ? 'btn-edit' : 'btn-add'}`}
                        onClick={(e) => { e.stopPropagation(); handleEditGroup(group); }}
                        title={group.hasSubscription ? 'Modifier l\'abonnement' : 'Ajouter un abonnement'}
                      >
                        <FontAwesomeIcon icon={group.hasSubscription ? faEdit : faFileContract} />
                        {group.hasSubscription ? 'Modifier' : 'Abonner'}
                      </button>
                    </td>
                  </tr>
                  {/* Expanded mobile detail row */}
                  {expandedSubId === group.id && (
                    <tr className="sub-expanded-row">
                      <td colSpan="8">
                        <div className="sub-expanded-card">
                          <div className="sub-expanded-grid">
                            <div className="sub-expanded-item">
                              <span className="sub-exp-label">Admin Responsable</span>
                              <span className="sub-exp-value">{group.subscription?.admin_name || '-'}</span>
                            </div>
                            <div className="sub-expanded-item">
                              <span className="sub-exp-label">Montant Annuel</span>
                              <span className="sub-exp-value">{formatAmount(group.subscription?.montant)}</span>
                            </div>
                            <div className="sub-expanded-item">
                              <span className="sub-exp-label">Pré-Abonnement</span>
                              <span className="sub-exp-value">{formatAmount(group.subscription?.montant_preabonnement)}</span>
                            </div>
                            <div className="sub-expanded-item">
                              <span className="sub-exp-label">Période</span>
                              <span className="sub-exp-value">
                                {group.subscription?.contract_start_date 
                                  ? `${formatDate(group.subscription.contract_start_date)} - ${formatDate(group.subscription.contract_end_date)}`
                                  : '-'}
                                {isExpiringSoon(group.subscription) && ' ⚠️'}
                              </span>
                            </div>
                          </div>
                          <div className="sub-expanded-actions">
                            {group.subscription && group.subscription.contract_pdf_url && (
                              <a 
                                href={`${api.defaults.baseURL?.replace('/api', '')}${group.subscription.contract_pdf_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="sub-exp-btn"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FontAwesomeIcon icon={faDownload} /> Contrat PDF
                              </a>
                            )}
                            <button 
                              className={`sub-exp-btn ${group.hasSubscription ? 'edit' : 'add'}`}
                              onClick={(e) => { e.stopPropagation(); handleEditGroup(group); }}
                            >
                              <FontAwesomeIcon icon={group.hasSubscription ? faEdit : faFileContract} />
                              {group.hasSubscription ? ' Modifier' : ' Abonner'}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          
          <div className="pagination-info">
            Page {currentPage} sur {totalPages}
            <span className="pagination-total">({filteredGroups.length} groupes)</span>
          </div>
          
          <button 
            className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editingGroup?.subscription ? 'Modifier l\'abonnement' : 'Nouvel abonnement'}
                <span className="modal-subtitle">{editingGroup?.nom}</span>
              </h2>
              <button className="close-btn" onClick={() => { setShowModal(false); resetForm(); }}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="group-info-banner">
                  <FontAwesomeIcon icon={faBuilding} />
                  <span>Groupe: <strong>{editingGroup?.nom}</strong></span>
                </div>

                <div className="form-group">
                  <label>Admin Responsable</label>
                  <div className="admin-display">
                    <span>{formData.admin_name || admin?.name || 'Non défini'}</span>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Montant Annuel (FCFA) *</label>
                    <input
                      type="number"
                      step="1"
                      value={formData.montant}
                      onChange={(e) => setFormData({ ...formData, montant: e.target.value })}
                      placeholder="Ex: 500000"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Pré-Abonnement (FCFA)</label>
                    <input
                      type="number"
                      step="1"
                      value={formData.montant_preabonnement}
                      onChange={(e) => setFormData({ ...formData, montant_preabonnement: e.target.value })}
                      placeholder="Ex: 100000"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Statut *</label>
                  <select
                    value={formData.statut}
                    onChange={handleStatusChange}
                    required
                  >
                    <option value="Active">Actif</option>
                    <option value="En attente">En attente</option>
                    <option value="Expiré">Expiré</option>
                    <option value="Annulé">Annulé</option>
                  </select>
                </div>

                {/* Show cancellation info if status is Annulé */}
                {formData.statut === 'Annulé' && (formData.cancellation_reason || formData.cancellation_date) && (
                  <div className="cancellation-info-box">
                    <div className="cancellation-header">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <span>Informations d'annulation</span>
                    </div>
                    <div className="cancellation-details">
                      <p><strong>Date d'annulation:</strong> {formatDate(formData.cancellation_date) || '-'}</p>
                      <p><strong>Raison:</strong> {formData.cancellation_reason || '-'}</p>
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>Date Début Contrat</label>
                    <input
                      type="date"
                      value={formData.contract_start_date}
                      onChange={(e) => setFormData({ ...formData, contract_start_date: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Date Fin Contrat</label>
                    <input
                      type="date"
                      value={formData.contract_end_date}
                      onChange={(e) => setFormData({ ...formData, contract_end_date: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3"
                    placeholder="Informations complémentaires..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  <FontAwesomeIcon icon={faSave} /> 
                  {editingGroup?.subscription ? 'Mettre à jour' : 'Créer l\'abonnement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cancellation Modal */}
      {showCancellationModal && (
        <div className="modal-overlay" onClick={() => setShowCancellationModal(false)}>
          <div className="modal-content cancellation-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header cancellation-header">
              <h2>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                Confirmer l'annulation
              </h2>
              <button className="close-btn" onClick={() => setShowCancellationModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="cancellation-warning">
                <p>Vous êtes sur le point d'annuler l'abonnement pour <strong>{editingGroup?.nom}</strong></p>
              </div>

              <div className="form-group">
                <label>Date d'annulation *</label>
                <input
                  type="date"
                  value={cancellationData.cancellation_date}
                  onChange={(e) => setCancellationData({ ...cancellationData, cancellation_date: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Raison de l'annulation *</label>
                <textarea
                  value={cancellationData.cancellation_reason}
                  onChange={(e) => setCancellationData({ ...cancellationData, cancellation_reason: e.target.value })}
                  rows="4"
                  placeholder="Décrivez la raison de l'annulation..."
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => setShowCancellationModal(false)}
              >
                Retour
              </button>
              <button 
                type="button" 
                className="btn-danger"
                onClick={handleConfirmCancellation}
                disabled={!cancellationData.cancellation_reason || !cancellationData.cancellation_date}
              >
                <FontAwesomeIcon icon={faExclamationTriangle} />
                Confirmer l'annulation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;
